import type { services } from '@cwos/api-client';
import type { AuthorityGateKind } from '../authorityGate.js';
import {
  nowLocalInput,
  toDatetimeLocalInput,
  type DraftErrors,
} from '../schedule/scheduleLogic.js';

/**
 * Visits screen logic — draft/validate/payload plumbing only. Pure functions,
 * unit-tested; no react-query and no router imports here.
 *
 * WHAT WAS MISSING WAS NOT A FORM, IT WAS THE FIRST TWO LINKS OF THE LOOP.
 * `POST /services/scheduled` (1757040000000) and `POST /services/events` have
 * both existed on the server with their own authority models, and neither had a
 * caller anywhere in `apps/admin/src` or `apps/worker-mobile/src`. So a visit
 * could not be PLANNED and could not be RECORDED, which makes everything
 * downstream unreachable by construction rather than merely unused:
 *
 *   - `apps/admin/src/features/delivery.ts` subtracts the delivered set from
 *     the planned one to find missed visits, and a subtraction from an empty
 *     set finds none, forever. A care-gap surface reading zero because its
 *     input is unreachable is indistinguishable from one reading zero because
 *     care is complete.
 *   - a `service_documentation_status` row exists per `service_event`, so the
 *     documentation queue was empty by construction and the worker app's
 *     wrap-up flow had nothing to document.
 *
 * `nowLocalInput` / `toDatetimeLocalInput` / `DraftErrors` are IMPORTED from
 * `../schedule/scheduleLogic.js` rather than copied. They solve the same problem
 * for the same reason — a `datetime-local` input is in the VIEWER's local time
 * and an ISO instant is not — and a second copy would be a second thing to drift
 * on the day somebody fixes a timezone edge in one of them.
 */

// --- the PLANNED visit ------------------------------------------------------

/**
 * A planned client visit, as the form holds it.
 *
 * EVERY FIELD IS A STRING, INCLUDING THE IDS, because a `<select>` value is one
 * and `''` is the shape "not chosen" actually arrives in. The payload builders
 * below are where `''` becomes an omitted key, and that conversion happening in
 * exactly one place is the point: `createScheduledServiceSchema` takes `clientId`
 * as `uuid.optional()`, so an empty string is a 400 and not a null.
 */
export interface PlannedVisitDraft {
  clientId: string;
  workerId: string;
  serviceTypeId: string;
  /** `datetime-local` input value, in the viewer's own local time. */
  startsAt: string;
  /** `datetime-local` input value, in the viewer's own local time. */
  endsAt: string;
  note: string;
}

/**
 * A blank plan: an hour from now, running an hour.
 *
 * NEITHER `clientId` NOR `workerId` IS PREFILLED, and the unstaffed row is not
 * an accident of that. 1757040000000 settled that a plan may name a time before
 * it names a worker — "the unstaffed row is the row the roster exists to show" —
 * so `validatePlannedVisit` requires a client and deliberately does not require
 * a worker.
 */
export function emptyPlannedVisitDraft(now: Date): PlannedVisitDraft {
  const start = new Date(now.getTime() + 60 * 60 * 1000);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    clientId: '',
    workerId: '',
    serviceTypeId: '',
    startsAt: nowLocalInput(start),
    endsAt: nowLocalInput(end),
    note: '',
  };
}

/** Pre-fills an edit draft from a planned visit as the server states it. */
export function plannedVisitDraftFrom(row: services.ScheduledService): PlannedVisitDraft {
  return {
    clientId: row.clientId ?? '',
    workerId: row.workerId ?? '',
    serviceTypeId: row.serviceTypeId ?? '',
    startsAt: toDatetimeLocalInput(row.startsAt),
    endsAt: row.endsAt ? toDatetimeLocalInput(row.endsAt) : '',
    note: row.note ?? '',
  };
}

/**
 * Validates a planned visit. A courtesy floor only — the server is the last
 * word, and its `superRefine` says the same thing about `endsAt`.
 *
 * A CLIENT IS REQUIRED HERE AND OPTIONAL ON THE WIRE. `createScheduledService
 * Schema` allows a plan with no client at all, which is the right server
 * contract for an imported roster; on this form it is a row nobody can work and
 * nobody will notice, which is the same argument the route makes for refusing a
 * soft-deleted worker. A WORKER is not required, on purpose: see
 * `emptyPlannedVisitDraft`.
 */
export function validatePlannedVisit(draft: PlannedVisitDraft): DraftErrors {
  const errors: DraftErrors = {};
  if (draft.clientId.trim() === '') {
    errors['clientId'] = 'Choose who this visit is for.';
  }
  if (draft.startsAt.trim() === '') {
    errors['startsAt'] = 'Choose when the visit starts.';
  }
  if (draft.startsAt && draft.endsAt) {
    const start = new Date(draft.startsAt);
    const end = new Date(draft.endsAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      errors['startsAt'] = errors['startsAt'] ?? 'That date and time is not valid.';
    } else if (end.getTime() < start.getTime()) {
      errors['endsAt'] = 'The visit cannot end before it starts.';
    }
  }
  if (draft.note.length > 2000) {
    errors['note'] = 'Keep the note under 2,000 characters.';
  }
  return errors;
}

/** Builds the `POST /services/scheduled` body from a validated draft. */
export function plannedVisitCreatePayload(
  draft: PlannedVisitDraft,
): services.CreateScheduledServiceInput {
  return {
    clientId: draft.clientId,
    ...(draft.workerId ? { workerId: draft.workerId } : {}),
    ...(draft.serviceTypeId ? { serviceTypeId: draft.serviceTypeId } : {}),
    startsAt: new Date(draft.startsAt).toISOString(),
    ...(draft.endsAt ? { endsAt: new Date(draft.endsAt).toISOString() } : {}),
    ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
  };
}

/**
 * Builds the `PATCH /services/scheduled/:id` body.
 *
 * `null` AND OMITTED ARE DIFFERENT REQUESTS AND THIS SENDS `null`. The patch
 * schema is a `.partial()` over nullable fields, so an omitted key leaves the
 * column as it stands and `null` clears it. A form that omitted a field the
 * reader had just emptied would silently decline to unstaff a visit — the
 * reader would clear the worker, save, and watch the old worker come back.
 *
 * `workerId` IS PATCHABLE HERE and deliberately is not on a delivered visit:
 * re-pointing a PLANNED visit at a different worker is what re-staffing a gap
 * consists of (`services/routes.ts` says so at the PATCH), while re-attributing
 * a DELIVERED one would rewrite who attested to care that already happened.
 */
export function plannedVisitUpdatePayload(
  draft: PlannedVisitDraft,
): services.UpdateScheduledServiceInput {
  return {
    clientId: draft.clientId || null,
    workerId: draft.workerId || null,
    serviceTypeId: draft.serviceTypeId || null,
    startsAt: new Date(draft.startsAt).toISOString(),
    endsAt: draft.endsAt ? new Date(draft.endsAt).toISOString() : null,
    note: draft.note.trim() || null,
  };
}

// --- the DELIVERED visit ----------------------------------------------------

/**
 * A delivered visit, as the form holds it.
 *
 * `minutes` IS A STRING AND NOT A NUMBER, and that is the one field worth
 * arguing about. `0` is a real, meaningful value here — a no-show the worker
 * travelled to is a delivery of zero minutes — and `''` means "this record does
 * not say". A numeric field cannot hold both: `Number('')` is `0`, so an
 * untouched box would assert a zero-minute visit for every record that simply
 * did not mention a duration. Kept as text, converted once, in
 * `deliveredVisitPayload`.
 *
 * `attendanceStatus` STARTS EMPTY. The server defaults it, so the form could
 * too — and defaulting it to `attended` would put a clinical claim in every
 * record the reader clicked past without reading. Whether the client was there
 * is the fact this row exists to state.
 */
export interface DeliveredVisitDraft {
  clientId: string;
  workerId: string;
  serviceTypeId: string;
  /** The plan this fulfilled, when there is one. See `deliveredVisitPayload`. */
  scheduledServiceId: string;
  /** `datetime-local` input value, in the viewer's own local time. */
  occurredAt: string;
  /** `datetime-local` input value, in the viewer's own local time. */
  endedAt: string;
  /** Free text so that "" and "0" stay different. See the interface note. */
  minutes: string;
  attendanceStatus: '' | services.AttendanceStatus;
  isBillable: boolean;
  note: string;
}

/** A blank record of a delivery, defaulting to "it happened just now". */
export function emptyDeliveredVisitDraft(now: Date): DeliveredVisitDraft {
  return {
    clientId: '',
    workerId: '',
    serviceTypeId: '',
    scheduledServiceId: '',
    occurredAt: nowLocalInput(now),
    endedAt: '',
    minutes: '',
    attendanceStatus: '',
    isBillable: false,
    note: '',
  };
}

/**
 * The record of a delivery, pre-filled from the plan it fulfilled.
 *
 * `scheduledServiceId` IS THE WHOLE REASON THIS FUNCTION EXISTS. It is the
 * column `deriveDeliveryToday` joins on when it subtracts the delivered set
 * from the planned one, so a record entered against a plan without it leaves
 * that plan in the difference — reported as a missed visit, with an evidence
 * link, that did not happen. Recording from the roster row carries the link;
 * recording from the blank form cannot, and does not pretend to.
 *
 * `attendanceStatus` IS NOT PRE-FILLED even though the plan is being fulfilled.
 * A visit reached the point of being recorded, not the point of having gone
 * well: it is exactly the no-shows that a prefilled `attended` would swallow.
 */
export function deliveredVisitDraftFromPlan(
  plan: services.ScheduledService,
  now: Date,
): DeliveredVisitDraft {
  const start = new Date(plan.startsAt);
  return {
    clientId: plan.clientId ?? '',
    workerId: plan.workerId ?? '',
    serviceTypeId: plan.serviceTypeId ?? '',
    scheduledServiceId: plan.id,
    occurredAt: Number.isNaN(start.getTime())
      ? nowLocalInput(now)
      : toDatetimeLocalInput(plan.startsAt),
    endedAt: plan.endsAt ? toDatetimeLocalInput(plan.endsAt) : '',
    minutes: '',
    attendanceStatus: '',
    isBillable: false,
    note: '',
  };
}

/**
 * Validates a record of a delivery. A courtesy floor; the server is the last
 * word and `createServiceEventSchema` says the same things.
 *
 * `mayAttributeToOthers` mirrors `service:write_all`. Without it the server does
 * not refuse a missing `workerId` — it silently fills in the caller's own worker
 * row — so this does not demand one either. Demanding a choice the reader is not
 * allowed to make is how a form invents a permission the server does not have.
 */
export function validateDeliveredVisit(
  draft: DeliveredVisitDraft,
  opts: { mayAttributeToOthers: boolean },
): DraftErrors {
  const errors: DraftErrors = {};
  if (draft.clientId.trim() === '') {
    errors['clientId'] = 'Choose who was seen.';
  }
  if (opts.mayAttributeToOthers && draft.workerId.trim() === '') {
    errors['workerId'] = 'Choose who delivered it.';
  }
  if (draft.occurredAt.trim() === '') {
    errors['occurredAt'] = 'Choose when the visit happened.';
  }
  if (draft.attendanceStatus === '') {
    errors['attendanceStatus'] = 'Say whether the client was seen.';
  }
  if (draft.occurredAt && draft.endedAt) {
    const start = new Date(draft.occurredAt);
    const end = new Date(draft.endedAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      errors['occurredAt'] = errors['occurredAt'] ?? 'That date and time is not valid.';
    } else if (end.getTime() < start.getTime()) {
      errors['endedAt'] = 'The visit cannot end before it started.';
    }
  }
  if (draft.minutes.trim() !== '') {
    const minutes = Number(draft.minutes);
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 24 * 60) {
      // The same bound `createServiceEventSchema` enforces: int, 0..1440.
      errors['minutes'] = 'Minutes must be a whole number between 0 and 1440.';
    }
  }
  if (draft.note.length > 2000) {
    errors['note'] = 'Keep the note under 2,000 characters.';
  }
  return errors;
}

/**
 * Builds the `POST /services/events` body from a validated draft.
 *
 * `workerId` IS OMITTED WHEN THE READER MAY NOT ATTRIBUTE TO OTHERS, and that
 * is not a nicety. `resolveDeliverySubject` refuses a `workerId` naming anyone
 * but the caller's own worker row, so sending one a plain `service:write` holder
 * merely inherited from a prefilled roster row would turn a legitimate recording
 * into a 403. Omitted, the server fills in their own id, which is what actually
 * happened.
 *
 * `minutes` IS SENT ONLY WHEN THE FORM SAYS SOMETHING. `''` means the record
 * does not state a duration; `'0'` means it states zero. See
 * {@link DeliveredVisitDraft}.
 */
export function deliveredVisitPayload(
  draft: DeliveredVisitDraft,
  opts: { mayAttributeToOthers: boolean },
): services.CreateServiceEventInput {
  const minutes = draft.minutes.trim();
  return {
    clientId: draft.clientId,
    ...(opts.mayAttributeToOthers && draft.workerId ? { workerId: draft.workerId } : {}),
    ...(draft.serviceTypeId ? { serviceTypeId: draft.serviceTypeId } : {}),
    ...(draft.scheduledServiceId ? { scheduledServiceId: draft.scheduledServiceId } : {}),
    occurredAt: new Date(draft.occurredAt).toISOString(),
    ...(draft.endedAt ? { endedAt: new Date(draft.endedAt).toISOString() } : {}),
    ...(minutes === '' ? {} : { minutes: Number(minutes) }),
    ...(draft.attendanceStatus === '' ? {} : { attendanceStatus: draft.attendanceStatus }),
    isBillable: draft.isBillable,
    source: 'manual',
    ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
  };
}

/**
 * The server's own message when it has one — a 400 from `createServiceEventSchema`
 * or a 404 naming a soft-deleted worker is far more use than a house sentence.
 * Same shape and same reasoning as `shiftErrorMessage`.
 */
export function visitErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message.trim() !== '') return err.message;
  return fallback;
}

/**
 * PH-UX-005 — the planned-visit status pill printed the enum (`no_show`,
 * `completed`, `scheduled`) on the one screen where a supervisor decides
 * whether a visit happened. The API contract (`services.ts`) warns that three
 * of the four values "read as failures of care" when shown raw. Every member
 * has a sentence here; `satisfies` makes a new member a type error rather
 * than a raw string on screen.
 */
const VISIT_STATUS_LABELS = {
  scheduled: 'Planned',
  completed: 'Delivered',
  no_show: 'Did not attend',
  cancelled: 'Called off',
} satisfies Record<services.ScheduledServiceStatus, string>;

export function visitStatusLabel(status: string): string {
  return (VISIT_STATUS_LABELS as Record<string, string>)[status] ?? 'Unknown status';
}

/** How the visit went, in the words a reader recognises. */
export const ATTENDANCE_LABELS: Record<services.AttendanceStatus, string> = {
  attended: 'The client was seen',
  no_show: 'The client did not attend',
  cancelled: 'Called off',
  partial: 'Partly delivered',
  // Imported only — the source's own attendance word was not one we could
  // classify. Never offered for manual entry.
  unknown: 'Attendance not classified by the source system',
};

/**
 * The planned rows worth showing, newest plan last.
 *
 * `cancelled` STAYS ON THE LIST. It is a state the plan carries and not a
 * deletion: "planned and then called off" and "never planned" are different
 * facts about a client's week, and only one of them is a care gap. Hiding the
 * cancelled row would make the two indistinguishable on the one screen where
 * somebody might act on the difference.
 */
export function visitsInWindow<T extends { startsAt: string }>(
  rows: readonly T[],
  from: Date,
  to: Date,
): T[] {
  const lo = from.getTime();
  const hi = to.getTime();
  return rows
    .filter((r) => {
      const t = new Date(r.startsAt).getTime();
      return !Number.isNaN(t) && t >= lo && t <= hi;
    })
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/** The delivered rows in the same window, most recent first. */
export function deliveriesInWindow<T extends { occurredAt: string }>(
  rows: readonly T[],
  from: Date,
  to: Date,
): T[] {
  const lo = from.getTime();
  const hi = to.getTime();
  return rows
    .filter((r) => {
      const t = new Date(r.occurredAt).getTime();
      return !Number.isNaN(t) && t >= lo && t <= hi;
    })
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

// ------------------------------------------------------------- client names

/**
 * WHY A CLIENT HAS NO NAME ON THIS SCREEN — five answers, and only one of them
 * is about the reader's permissions.
 *
 * `Visits.tsx` built this inline and got the ORDER wrong, in the way
 * `docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-FRONTEND.md` calls the sharpest
 * of the seven. The two query branches were right there —
 *
 *     if (!has('directory:read')) return 'A client you cannot view';
 *     if (clients.isError) return 'Name unavailable';
 *     if (clients.data === undefined) return 'Loading…';
 *
 * — but they interrogate the CLIENTS query, and nothing interrogated the
 * PERMISSIONS query. `has()` is `false` while `/auth/me` is in flight and
 * `false` after it failed, so in that window a reader holding `directory:read`
 * was told, name by name down the list, that these were people they may not
 * see.
 *
 * IT IS A FUNCTION AND NOT A CLOSURE IN THE SCREEN because of what that costs
 * to test. Inside `Visits`, the screen-level `service:read` gate resolves from
 * the SAME `/auth/me` query, so it refuses the whole screen before any row
 * renders and two of the five branches below cannot be reached by any mount —
 * they would be untested code that reads as covered. Out here every branch is
 * one call, and the ordering is the thing under test rather than a side effect
 * of which gate happens to fire first.
 *
 * The shape mirrors `listState.nameResolver`, which draws the same distinction
 * for workers; the difference is that this one takes the four-state gate rather
 * than a boolean, because a boolean is what collapsed the three.
 *
 * INITIALS ARE THE ONLY NAME THIS SURFACE EVER LEARNS. `GET /directory/clients`
 * serves `ClientListView`, which carries no full name — that is one audited
 * reveal on one client (`client:reveal_name`) and not something a roster
 * assembles for itself.
 */
export const CLIENT_NONE = 'No client named';
export const CLIENT_LOADING = 'Loading…';
export const CLIENT_UNAVAILABLE = 'Name unavailable';
export const CLIENT_OUT_OF_SCOPE = 'A client you cannot view';

export interface ClientNameSource {
  data: readonly { id: string; initials: string }[] | undefined;
  isError: boolean;
}

export function clientNameResolver(
  /**
   * The reader's answer for `directory:read`, as a KIND rather than a boolean.
   * `refused` is the only one entitled to `CLIENT_OUT_OF_SCOPE`.
   */
  directoryGate: AuthorityGateKind,
  source: ClientNameSource,
): (id: string | null) => string {
  const map = new Map<string, string>();
  for (const c of source.data ?? []) map.set(c.id, c.initials);
  return (id: string | null): string => {
    if (id === null) return CLIENT_NONE;
    const known = map.get(id);
    if (known !== undefined) return known;
    // The permission answer FIRST, because it is the one that was being
    // borrowed. Ordered most-certain-first within it: still checking, could not
    // check, and only then the refusal.
    if (directoryGate === 'unresolved') return CLIENT_LOADING;
    if (directoryGate === 'unknown') return CLIENT_UNAVAILABLE;
    if (directoryGate === 'refused') return CLIENT_OUT_OF_SCOPE;
    if (source.isError) return CLIENT_UNAVAILABLE;
    if (source.data === undefined) return CLIENT_LOADING;
    // The directory answered, the reader may read it, and this id was not in
    // it: scope is the honest reading and this is the only branch left
    // entitled to say so.
    return CLIENT_OUT_OF_SCOPE;
  };
}
